import axios from 'axios';
import { useEffect, useState } from 'react';
import './CandidateDashboard.css';

const Education = ({ userId }) => {
    const BASE_API_URL = process.env.REACT_APP_API_URL;

    const [educationList, setEducationList] = useState([]);

    const [educationDetails, setEducationDetails] = useState({
        degree: '',
        branch: '',
        percentage: '',
        college: ''
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [showForm, setShowForm] = useState(false);

    // Fetch existing education details
    useEffect(() => {
        const fetchEducation = async () => {
            try {
                const response = await axios.get(
                    `${BASE_API_URL}/getEducationsByUserId`,
                    {
                        params: { userId }
                    }
                );

                setEducationList(response.data);

                // Show form only if no education records exist
                setShowForm(response.data.length === 0);

            } catch (error) {
                console.error(
                    'Failed to fetch education details',
                    error
                );

                setErrorMessage(
                    'Could not load education details.'
                );
            }
        };

        if (userId) {
            fetchEducation();
        }
    }, [userId, BASE_API_URL]);

    // Handle input changes
    const handleEducationChange = (e) => {
        const { name, value } = e.target;

        setEducationDetails({
            ...educationDetails,
            [name]: value
        });
    };

    // Open Add Education form
    const handleAddEducationClick = () => {
        setEducationDetails({
            degree: '',
            branch: '',
            percentage: '',
            college: ''
        });

        setErrorMessage('');
        setShowForm(true);
    };

    // Submit new education detail
    const handleAddEducation = async () => {
        const {
            degree,
            branch,
            percentage,
            college
        } = educationDetails;

        if (!degree || !branch || !percentage || !college) {
            setErrorMessage('All fields are required.');
            return;
        }

        try {
            const response = await axios.put(
                `${BASE_API_URL}/setNewEducationByUserId`,
                educationDetails,
                {
                    params: { userId }
                }
            );

            /*
             * Backend may return the complete updated list.
             * If it does, use response.data.
             * Otherwise, refresh the list from backend.
             */
            const updatedResponse = await axios.get(
                `${BASE_API_URL}/getEducationsByUserId`,
                {
                    params: { userId }
                }
            );

            setEducationList(updatedResponse.data);

            // Clear form
            setEducationDetails({
                degree: '',
                branch: '',
                percentage: '',
                college: ''
            });

            setErrorMessage('');
            setShowForm(false);

        } catch (error) {
            console.error(
                'Failed to add education detail',
                error
            );

            setErrorMessage(
                'Failed to add education. Try again.'
            );
        }
    };

    // Delete education entry using education ID
    const handleDeleteEducation = async (educationId) => {

        if (!educationId) {
            setErrorMessage(
                'This education record does not have an ID. Please use the cleanup option for old records.'
            );
            return;
        }

        try {
            const response = await axios.put(
                `${BASE_API_URL}/deleteEducationByUserId`,
                null,
                {
                    params: {
                        userId: userId,
                        educationId: educationId
                    }
                }
            );

            // Backend returns updated education list
            setEducationList(response.data);

            // Show form if no records are left
            if (response.data.length === 0) {
                setShowForm(true);
            }

            setErrorMessage('');

        } catch (error) {
            console.error(
                'Failed to delete education detail',
                error
            );

            setErrorMessage(
                'Failed to delete education detail.'
            );
        }
    };

    return (
        <div className="candidate-profile-details">

            {/* Header */}
            <div
                className="d-flex justify-content-between align-items-center mb-4"
                style={{
                    borderBottom: '1px solid #e5e7eb',
                    paddingBottom: '12px'
                }}
            >
                <h4 className="mb-0">
                    Education Details
                </h4>

                {!showForm && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddEducationClick}
                        style={{
                            borderRadius: '8px',
                            padding: '8px 16px',
                            fontWeight: '500'
                        }}
                    >
                        + Add Education
                    </button>
                )}
            </div>

            {/* Error Message */}
            {errorMessage && (
                <div
                    className="alert alert-danger"
                    role="alert"
                >
                    {errorMessage}
                </div>
            )}

            {/* Existing Education Cards */}
            {educationList.length > 0 && (
                <div className="existing-education mb-4">

                    <h5 className="mb-3">
                        Saved Education
                    </h5>

                    <div className="row">

                        {educationList.map((edu, index) => (

                            <div
                                className="col-md-6 mb-4"
                                key={edu.id || index}
                            >

                                <div
                                    className="card h-100 shadow-sm"
                                    style={{
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        overflow: 'hidden'
                                    }}
                                >

                                    <div
                                        className="card-body"
                                        style={{
                                            padding: '20px',
                                            position: 'relative'
                                        }}
                                    >

                                        {/* Remove Button */}
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() =>
                                                handleDeleteEducation(edu.id)
                                            }
                                            style={{
                                                position: 'absolute',
                                                top: '15px',
                                                right: '15px',
                                                borderRadius: '6px',
                                                padding: '5px 10px'
                                            }}
                                            title="Remove education"
                                        >
                                            Remove
                                        </button>

                                        {/* Degree */}
                                        <h5
                                            className="card-title mb-2"
                                            style={{
                                                paddingRight: '90px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {edu.degree} 
                                        </h5>

                                        {/* Branch */}
                                        <h6
                                            className="card-subtitle mb-3 text-muted"
                                        >
                                            {edu.branch}
                                        </h6>

                                        {/* Details */}
                                        <div className="card-text">

                                            <div className="mb-2">
                                                <strong>
                                                    College:
                                                </strong>
                                                <br />
                                                <span>
                                                    {edu.college}
                                                </span>
                                            </div>

                                            <div>
                                                <strong>
                                                    Percentage/CGPA:
                                                </strong>
                                                <br />
                                                <span>
                                                    {edu.percentage}
                                                </span>
                                            </div>

                                        </div>

                                    </div>

                                </div>

                            </div>

                        ))}

                    </div>

                </div>
            )}

            {/* Add New Education Form */}
            {showForm && (
                <div
                    className="candidate-profile-details"
                    style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '20px',
                        background: '#fff'
                    }}
                >

                    {/* Form Header */}
                    <div
                        className="d-flex justify-content-between align-items-center mb-4"
                    >
                        <h5 className="mb-0">
                            Add Education
                        </h5>

                        {educationList.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-light btn-sm"
                                onClick={() => {
                                    setShowForm(false);
                                    setErrorMessage('');
                                }}
                                style={{
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px'
                                }}
                            >
                                Cancel
                            </button>
                        )}
                    </div>

                    <div className="container-fluid">

                        {/* Degree + Branch */}
                        <div className="row mb-3">

                            {/* Degree */}
                            <div className="col-md-6 mb-3 mb-md-0">

                                <div className="form-group">

                                    <label
                                        htmlFor="degree"
                                        className="required-label mb-2"
                                    >
                                        <b>
                                            Last Education Degree:
                                        </b>
                                    </label>

                                    <input
                                        type="text"
                                        id="degree"
                                        name="degree"
                                        className="form-control"
                                        value={educationDetails.degree}
                                        onChange={handleEducationChange}
                                        placeholder="Enter your last education degree"
                                    />

                                </div>

                            </div>

                            {/* Branch */}
                            <div className="col-md-6">

                                <div className="form-group">

                                    <label
                                        htmlFor="branch"
                                        className="required-label mb-2"
                                    >
                                        <b>
                                            Branch Name:
                                        </b>
                                    </label>

                                    <input
                                        type="text"
                                        id="branch"
                                        name="branch"
                                        className="form-control"
                                        value={educationDetails.branch}
                                        onChange={handleEducationChange}
                                        placeholder="Enter branch name"
                                    />

                                </div>

                            </div>

                        </div>

                        {/* Percentage + College */}
                        <div className="row mb-3">

                            {/* Percentage */}
                            <div className="col-md-6 mb-3 mb-md-0">

                                <div className="form-group">

                                    <label
                                        htmlFor="percentage"
                                        className="required-label mb-2"
                                    >
                                        <b>
                                            Percentage/CGPA:
                                        </b>
                                    </label>

                                    <input
                                        type="text"
                                        id="percentage"
                                        name="percentage"
                                        className="form-control"
                                        value={educationDetails.percentage}
                                        onChange={handleEducationChange}
                                        placeholder="Enter percentage or CGPA"
                                    />

                                </div>

                            </div>

                            {/* College */}
                            <div className="col-md-6">

                                <div className="form-group">

                                    <label
                                        htmlFor="college"
                                        className="required-label mb-2"
                                    >
                                        <b>
                                            College/University:
                                        </b>
                                    </label>

                                    <input
                                        type="text"
                                        id="college"
                                        name="college"
                                        className="form-control"
                                        value={educationDetails.college}
                                        onChange={handleEducationChange}
                                        placeholder="Enter college or university name"
                                    />

                                </div>

                            </div>

                        </div>

                        {/* Save Button */}
                        <div className="d-flex justify-content-end mt-4">

                            <button
                                type="button"
                                onClick={handleAddEducation}
                                className="btn btn-primary"
                                style={{
                                    borderRadius: '8px',
                                    padding: '9px 20px',
                                    fontWeight: '500'
                                }}
                            >
                                Save Education
                            </button>

                        </div>

                    </div>

                </div>
            )}

        </div>
    );
};

export default Education;